/**
 * Configurations Settings Page
 *
 * Workspace admins can:
 * - Show/hide system (pre-built) configurations from the ConfigurationSelector
 * - Create workspace-custom configurations
 * - Archive custom configurations
 *
 * Uses migration 286 (workspace_configuration_settings table + RPCs).
 */

import { useEffect, useState } from 'react';
import {
  Layers,
  Plus,
  Archive,
  AlertCircle,
  Loader2,
  Eye,
  EyeOff,
  X,
  Info,
} from 'lucide-react';
import { PageHeader } from '../../../components/prioritization/PageHeader';
import { useConfigurationsStore } from '../../../stores/prioritization/configurationsStore';
import { useTenantStore } from '../../../stores/prioritization/tenantStore';
import { useUserStore } from '../../../stores/prioritization/userStore';
import { METHOD_REGISTRY } from '../../../lib/methodRegistry';
import { isToolSwitchedOff } from '../../../lib/switchedOffTools';
import type { ConfigurationWithVisibility } from '../../../stores/prioritization/configurationsStore';
import type {
  StructuralFootprint,
  WorkflowRequired,
  PersistenceDestination,
  FailureMode,
} from '../../../types/prioritization/configuration';
import {
  FOOTPRINT_LABELS,
  FAILURE_MODE_LABELS,
} from '../../../types/prioritization/configuration';

// ── Constants ──────────────────────────────────────────────────────────────────

const FOOTPRINT_OPTIONS: StructuralFootprint[] = ['zero', 'minimal', 'light', 'moderate', 'full'];
const WORKFLOW_OPTIONS: WorkflowRequired[] = ['none', 'workspace_default', 'explicit'];
const DESTINATION_OPTIONS: PersistenceDestination[] = ['personal', 'group', 'cycle'];
const FAILURE_MODE_OPTIONS: FailureMode[] = ['forgetting', 'friction', 'drift'];

const WORKFLOW_LABELS: Record<WorkflowRequired, string> = {
  none: 'None',
  workspace_default: 'Workspace default',
  explicit: 'Explicit (required)',
};

const DESTINATION_LABELS: Record<PersistenceDestination, string> = {
  personal: 'Personal',
  group: 'Group',
  cycle: 'Cycle',
};

const ALL_TOOLS = METHOD_REGISTRY.map((m) => ({
  toolId: m.variants[0]?.toolId ?? m.methodId,
  name: m.name,
})).filter((t) => !isToolSwitchedOff(t.toolId));

// ── Toggle switch ──────────────────────────────────────────────────────────────

function Toggle({
  enabled,
  onChange,
  disabled,
}: {
  enabled: boolean;
  onChange: (v: boolean) => void;
  disabled?: boolean;
}) {
  return (
    <button
      type="button"
      role="switch"
      aria-checked={enabled}
      disabled={disabled}
      onClick={() => onChange(!enabled)}
      className={`relative inline-flex h-5 w-9 items-center rounded-full transition-colors focus:outline-none focus:ring-2 focus:ring-primary-400 focus:ring-offset-1 ${
        enabled ? 'bg-primary-500' : 'bg-[var(--hover-tint)]'
      } ${disabled ? 'opacity-50 cursor-not-allowed' : 'cursor-pointer'}`}
    >
      <span
        className={`inline-block h-4 w-4 transform rounded-full bg-[var(--bg-raised)] shadow transition-transform ${
          enabled ? 'translate-x-4' : 'translate-x-0.5'
        }`}
      />
    </button>
  );
}

// ── System config card ─────────────────────────────────────────────────────────

function SystemConfigRow({
  config,
  onToggle,
  toggling,
}: {
  config: ConfigurationWithVisibility;
  onToggle: (configId: string, isVisible: boolean) => void;
  toggling: boolean;
}) {
  return (
    <div
      className={`flex items-center gap-4 px-4 py-3  border transition-colors ${
        config.is_visible
          ? 'bg-[var(--bg-raised)] border-[var(--rule)]'
          : 'bg-[var(--surface)] border-[var(--rule)] opacity-60'
      }`}
    >
      <div className="flex-1 min-w-0">
        <div className="flex items-center gap-2 flex-wrap">
          <span className="text-sm font-medium text-[var(--text-pri)]">{config.name}</span>
          <span className="inline-flex items-center px-1.5 py-0.5 text-[10px] font-medium bg-[var(--hover-tint)] text-[var(--text-ter)] rounded">
            {FOOTPRINT_LABELS[config.structural_footprint]}
          </span>
          <span className="inline-flex items-center px-1.5 py-0.5 text-[10px] font-medium bg-[var(--slate-dim)] text-[var(--text-sec)] rounded">
            {DESTINATION_LABELS[config.persistence_destination]}
          </span>
        </div>
        {config.description && (
          <p className="text-xs text-[var(--text-ter)] mt-0.5 truncate">{config.description}</p>
        )}
      </div>
      <div className="flex items-center gap-2 flex-shrink-0">
        {config.is_visible ? (
          <Eye className="h-3.5 w-3.5 text-[var(--text-ter)]" />
        ) : (
          <EyeOff className="h-3.5 w-3.5 text-[var(--text-ter)]" />
        )}
        <Toggle
          enabled={config.is_visible}
          onChange={(v) => onToggle(config.id, v)}
          disabled={toggling}
        />
      </div>
    </div>
  );
}

// ── Create custom config modal ─────────────────────────────────────────────────

interface CreateConfigForm {
  name: string;
  description: string;
  structural_footprint: StructuralFootprint;
  workflow_required: WorkflowRequired;
  persistence_destination: PersistenceDestination;
  primary_failure_modes: FailureMode[];
  default_tool_recommendations: string[];
}

const EMPTY_FORM: CreateConfigForm = {
  name: '',
  description: '',
  structural_footprint: 'minimal',
  workflow_required: 'none',
  persistence_destination: 'personal',
  primary_failure_modes: [],
  default_tool_recommendations: [],
};

function CreateConfigModal({
  workspaceId,
  userId,
  onClose,
  onCreate,
}: {
  workspaceId: string;
  userId: string;
  onClose: () => void;
  onCreate: () => void;
}) {
  const [form, setForm] = useState<CreateConfigForm>(EMPTY_FORM);
  const [saving, setSaving] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const createCustomConfiguration = useConfigurationsStore((s) => s.createCustomConfiguration);

  const toggleFailureMode = (mode: FailureMode) => {
    setForm((f) => ({
      ...f,
      primary_failure_modes: f.primary_failure_modes.includes(mode)
        ? f.primary_failure_modes.filter((m) => m !== mode)
        : [...f.primary_failure_modes, mode],
    }));
  };

  const toggleTool = (toolId: string) => {
    setForm((f) => ({
      ...f,
      default_tool_recommendations: f.default_tool_recommendations.includes(toolId)
        ? f.default_tool_recommendations.filter((t) => t !== toolId)
        : [...f.default_tool_recommendations, toolId],
    }));
  };

  const handleSubmit = async () => {
    if (!form.name.trim()) { setError('Name is required.'); return; }
    setSaving(true);
    setError(null);
    const slug = form.name.toLowerCase().replace(/[^a-z0-9]+/g, '-').replace(/^-|-$/g, '');
    const result = await createCustomConfiguration({
      workspace_id: workspaceId,
      name: form.name.trim(),
      slug,
      description: form.description.trim() || null,
      status: 'active',
      structural_footprint: form.structural_footprint,
      catchball_applicability: 'optional',
      workflow_required: form.workflow_required,
      cycle_template_used: false,
      primary_failure_modes: form.primary_failure_modes,
      object_types: [],
      output_shapes: [],
      persistence_destination: form.persistence_destination,
      configuration: {
        entry_point: '',
        framing_depth: 'minimal',
        decision_structure: 'open',
        default_tool_recommendations: form.default_tool_recommendations,
        promotion_path: '',
      },
      created_by: userId,
    });
    setSaving(false);
    if (!result) { setError('Failed to create configuration. Please try again.'); return; }
    onCreate();
  };

  return (
    <div className="fixed inset-0 z-50 flex items-center justify-center p-4">
      <div className="absolute inset-0 bg-black/40" onClick={onClose} />
      <div className="relative bg-[var(--bg-raised)] shadow-2xl border border-[var(--rule)] w-full max-w-lg max-h-[90vh] flex flex-col">
        {/* Header */}
        <div className="flex items-center justify-between px-6 py-4 border-b border-[var(--rule)]">
          <h3 className="text-base font-semibold text-[var(--text-pri)]">New Custom Configuration</h3>
          <button onClick={onClose} className="p-1 text-[var(--text-ter)] hover:text-[var(--text-sec)] hover:bg-[var(--hover-tint)]">
            <X className="h-4 w-4" />
          </button>
        </div>

        {/* Body */}
        <div className="overflow-y-auto flex-1 px-6 py-5 space-y-4">
          {error && (
            <div className="flex items-center gap-2 p-3 bg-[var(--coral-dim)] border border-[var(--coral)] text-sm text-[var(--coral)]">
              <AlertCircle className="h-4 w-4 flex-shrink-0" />
              {error}
            </div>
          )}

          {/* Name */}
          <div>
            <label className="block text-sm font-medium text-[var(--text-sec)] mb-1">
              Name <span className="text-[var(--coral)]">*</span>
            </label>
            <input
              type="text"
              value={form.name}
              onChange={(e) => setForm((f) => ({ ...f, name: e.target.value }))}
              placeholder="e.g., Quarterly Sprint Prioritization"
              className="w-full px-3 py-2 text-sm border border-[var(--rule)] focus:ring-2 focus:ring-primary-500 focus:border-primary-500"
              autoFocus
            />
          </div>

          {/* Description */}
          <div>
            <label className="block text-sm font-medium text-[var(--text-sec)] mb-1">Description</label>
            <textarea
              value={form.description}
              onChange={(e) => setForm((f) => ({ ...f, description: e.target.value }))}
              placeholder="What is this configuration for?"
              rows={2}
              className="w-full px-3 py-2 text-sm border border-[var(--rule)] focus:ring-2 focus:ring-primary-500 focus:border-primary-500 resize-none"
            />
          </div>

          {/* Footprint + Destination row */}
          <div className="grid grid-cols-2 gap-3">
            <div>
              <label className="block text-sm font-medium text-[var(--text-sec)] mb-1">Structural Footprint</label>
              <select
                value={form.structural_footprint}
                onChange={(e) => setForm((f) => ({ ...f, structural_footprint: e.target.value as StructuralFootprint }))}
                className="w-full px-3 py-2 text-sm bg-[var(--bg-raised)] border border-[var(--rule)] focus:ring-2 focus:ring-primary-500 focus:border-primary-500"
              >
                {FOOTPRINT_OPTIONS.map((fp) => (
                  <option key={fp} value={fp}>{FOOTPRINT_LABELS[fp]}</option>
                ))}
              </select>
            </div>
            <div>
              <label className="block text-sm font-medium text-[var(--text-sec)] mb-1">Saves to</label>
              <select
                value={form.persistence_destination}
                onChange={(e) => setForm((f) => ({ ...f, persistence_destination: e.target.value as PersistenceDestination }))}
                className="w-full px-3 py-2 text-sm bg-[var(--bg-raised)] border border-[var(--rule)] focus:ring-2 focus:ring-primary-500 focus:border-primary-500"
              >
                {DESTINATION_OPTIONS.map((d) => (
                  <option key={d} value={d}>{DESTINATION_LABELS[d]}</option>
                ))}
              </select>
            </div>
          </div>

          {/* Workflow required */}
          <div>
            <label className="block text-sm font-medium text-[var(--text-sec)] mb-1">Workflow Required</label>
            <select
              value={form.workflow_required}
              onChange={(e) => setForm((f) => ({ ...f, workflow_required: e.target.value as WorkflowRequired }))}
              className="w-full px-3 py-2 text-sm bg-[var(--bg-raised)] border border-[var(--rule)] focus:ring-2 focus:ring-primary-500 focus:border-primary-500"
            >
              {WORKFLOW_OPTIONS.map((w) => (
                <option key={w} value={w}>{WORKFLOW_LABELS[w]}</option>
              ))}
            </select>
            <p className="text-xs text-[var(--text-ter)] mt-1">
              "None" lets users skip cycle setup and go straight to a session.
            </p>
          </div>

          {/* Failure modes */}
          <div>
            <label className="block text-sm font-medium text-[var(--text-sec)] mb-2">Failure Modes Addressed</label>
            <div className="flex flex-wrap gap-2">
              {FAILURE_MODE_OPTIONS.map((mode) => {
                const active = form.primary_failure_modes.includes(mode);
                return (
                  <button
                    key={mode}
                    type="button"
                    onClick={() => toggleFailureMode(mode)}
                    className={`px-3 py-1.5 text-xs font-medium rounded-full border transition-colors ${
                      active
                        ? 'bg-primary-100 border-primary-300 text-primary-700'
                        : 'bg-[var(--bg-raised)] border-[var(--rule)] text-[var(--text-ter)] hover:border-[var(--rule)]'
                    }`}
                  >
                    {FAILURE_MODE_LABELS[mode]}
                  </button>
                );
              })}
            </div>
          </div>

          {/* Default tool recommendations */}
          <div>
            <label className="block text-sm font-medium text-[var(--text-sec)] mb-1">
              Recommended Tools
            </label>
            <p className="text-xs text-[var(--text-ter)] mb-2">
              These are surfaced as quick-launch suggestions when a user selects this configuration.
            </p>
            <div className="border border-[var(--rule)] overflow-hidden max-h-48 overflow-y-auto">
              {ALL_TOOLS.map((tool) => {
                const active = form.default_tool_recommendations.includes(tool.toolId);
                return (
                  <label
                    key={tool.toolId}
                    className={`flex items-center gap-3 px-3 py-2 cursor-pointer transition-colors ${
                      active ? 'bg-primary-50' : 'hover:bg-[var(--surface)]'
                    }`}
                  >
                    <input
                      type="checkbox"
                      checked={active}
                      onChange={() => toggleTool(tool.toolId)}
                      className="h-3.5 w-3.5 rounded border-[var(--rule)] text-primary-600 focus:ring-primary-500"
                    />
                    <span className="text-sm text-[var(--text-sec)]">{tool.name}</span>
                  </label>
                );
              })}
            </div>
          </div>
        </div>

        {/* Footer */}
        <div className="flex items-center justify-end gap-3 px-6 py-4 border-t border-[var(--rule)]">
          <button
            type="button"
            onClick={onClose}
            disabled={saving}
            className="px-4 py-2 text-sm font-medium text-[var(--text-sec)] hover:bg-[var(--surface)] border border-[var(--rule)] transition-colors"
          >
            Cancel
          </button>
          <button
            type="button"
            onClick={() => void handleSubmit()}
            disabled={saving || !form.name.trim()}
            className="inline-flex items-center gap-2 px-4 py-2 text-sm font-medium text-white bg-primary-600 hover:bg-primary-700 disabled:opacity-50 transition-colors"
          >
            {saving && <Loader2 className="h-3.5 w-3.5 animate-spin" />}
            {saving ? 'Creating…' : 'Create Configuration'}
          </button>
        </div>
      </div>
    </div>
  );
}

// ── Custom config row ──────────────────────────────────────────────────────────

function CustomConfigRow({
  config,
  onArchive,
  archiving,
}: {
  config: ConfigurationWithVisibility;
  onArchive: (id: string, name: string) => void;
  archiving: boolean;
}) {
  return (
    <div className="flex items-center gap-4 px-4 py-3 border border-[var(--rule)] bg-[var(--bg-raised)]">
      <div className="flex-1 min-w-0">
        <div className="flex items-center gap-2 flex-wrap">
          <span className="text-sm font-medium text-[var(--text-pri)]">{config.name}</span>
          <span className="inline-flex items-center px-1.5 py-0.5 text-[10px] font-medium bg-[var(--hover-tint)] text-[var(--text-ter)] rounded">
            {FOOTPRINT_LABELS[config.structural_footprint]}
          </span>
          <span className="inline-flex items-center px-1.5 py-0.5 text-[10px] font-medium bg-[var(--mustard-dim)] text-[var(--mustard)] rounded">
            Custom
          </span>
        </div>
        {config.description && (
          <p className="text-xs text-[var(--text-ter)] mt-0.5 truncate">{config.description}</p>
        )}
      </div>
      <button
        type="button"
        onClick={() => onArchive(config.id, config.name)}
        disabled={archiving}
        className="flex-shrink-0 p-1.5 text-[var(--text-ter)] hover:text-[var(--coral)] hover:bg-[var(--coral-dim)] transition-colors disabled:opacity-50"
        title="Archive configuration"
      >
        <Archive className="h-4 w-4" />
      </button>
    </div>
  );
}

// ── Main page ──────────────────────────────────────────────────────────────────

export default function ConfigurationsSettings() {
  const currentWorkspaceId = useTenantStore((s) => s.currentWorkspaceId);
  const userId = useUserStore((s) => s.userId) ?? 'system';

  const {
    allConfigsWithVisibility,
    visibilityLoading,
    fetchAllWithVisibility,
    toggleVisibility,
    archiveCustomConfiguration,
  } = useConfigurationsStore();

  const [togglingId, setTogglingId] = useState<string | null>(null);
  const [archivingId, setArchivingId] = useState<string | null>(null);
  const [showCreateModal, setShowCreateModal] = useState(false);
  const [error, setError] = useState<string | null>(null);

  useEffect(() => {
    if (currentWorkspaceId) {
      void fetchAllWithVisibility(currentWorkspaceId);
    }
  }, [currentWorkspaceId, fetchAllWithVisibility]);

  const handleToggle = async (configId: string, isVisible: boolean) => {
    if (!currentWorkspaceId) return;
    setTogglingId(configId);
    setError(null);
    const ok = await toggleVisibility(currentWorkspaceId, configId, isVisible, userId);
    setTogglingId(null);
    if (!ok) setError('Failed to update visibility. Please try again.');
  };

  const handleArchive = async (id: string, name: string) => {
    if (!confirm(`Archive "${name}"? It will no longer appear in the configuration selector.`)) return;
    setArchivingId(id);
    setError(null);
    const ok = await archiveCustomConfiguration(id);
    setArchivingId(null);
    if (!ok) setError('Failed to archive configuration. Please try again.');
  };

  const systemConfigs = allConfigsWithVisibility.filter((c) => c.is_system);
  const customConfigs = allConfigsWithVisibility.filter((c) => !c.is_system);
  const visibleCount = systemConfigs.filter((c) => c.is_visible).length;

  return (
    <div className="space-y-6">
      <PageHeader
        icon={<Layers className="h-4 w-4 text-primary-600" />}
        iconBg="bg-primary-50"
        title="Starting Points"
        subtitle="Control which starting points appear in the prioritization launcher. Hide ones that don't fit your team's workflow, or create custom configurations."
        actions={
          <button
            type="button"
            onClick={() => setShowCreateModal(true)}
            className="inline-flex items-center gap-2 px-3 py-2 text-sm font-medium text-white bg-primary-600 hover:bg-primary-700 transition-colors"
          >
            <Plus className="h-4 w-4" />
            New Configuration
          </button>
        }
      />

      {error && (
        <div className="flex items-center gap-2 p-3 bg-[var(--coral-dim)] border border-[var(--coral)] text-sm text-[var(--coral)]">
          <AlertCircle className="h-4 w-4 flex-shrink-0" />
          {error}
          <button onClick={() => setError(null)} className="ml-auto p-0.5 rounded hover:bg-[var(--coral-dim)]">
            <X className="h-3 w-3" />
          </button>
        </div>
      )}

      {/* System configurations */}
      <div>
        <div className="flex items-center justify-between mb-3">
          <div>
            <h2 className="text-sm font-semibold text-[var(--text-pri)]">Pre-built Configurations</h2>
            <p className="text-xs text-[var(--text-ter)] mt-0.5">
              {visibleCount} of {systemConfigs.length} visible in the launcher
            </p>
          </div>
          <div className="flex items-center gap-1.5 text-xs text-[var(--text-ter)]">
            <Info className="h-3.5 w-3.5" />
            Toggling off hides a config from all workspace members
          </div>
        </div>

        {visibilityLoading ? (
          <div className="flex items-center gap-2 text-sm text-[var(--text-ter)] py-6 justify-center">
            <Loader2 className="h-4 w-4 animate-spin" />
            Loading configurations…
          </div>
        ) : (
          <div className="space-y-2">
            {systemConfigs.map((config) => (
              <SystemConfigRow
                key={config.id}
                config={config}
                onToggle={handleToggle}
                toggling={togglingId === config.id}
              />
            ))}
          </div>
        )}
      </div>

      {/* Custom configurations */}
      <div>
        <div className="flex items-center justify-between mb-3">
          <div>
            <h2 className="text-sm font-semibold text-[var(--text-pri)]">Custom Configurations</h2>
            <p className="text-xs text-[var(--text-ter)] mt-0.5">
              Workspace-specific starting points you've created
            </p>
          </div>
        </div>

        {!visibilityLoading && customConfigs.length === 0 && (
          <div className="flex flex-col items-center justify-center py-10 border border-dashed border-[var(--rule)] text-center">
            <Layers className="h-8 w-8 text-[var(--text-ter)] mb-3" />
            <p className="text-sm font-medium text-[var(--text-ter)]">No custom configurations yet</p>
            <p className="text-xs text-[var(--text-ter)] mt-1 mb-4">
              Create one to add a workspace-specific starting point to the launcher.
            </p>
            <button
              type="button"
              onClick={() => setShowCreateModal(true)}
              className="inline-flex items-center gap-1.5 px-3 py-2 text-sm font-medium text-primary-600 border border-primary-200 hover:bg-primary-50 transition-colors"
            >
              <Plus className="h-3.5 w-3.5" />
              Create your first configuration
            </button>
          </div>
        )}

        {customConfigs.length > 0 && (
          <div className="space-y-2">
            {customConfigs.map((config) => (
              <CustomConfigRow
                key={config.id}
                config={config}
                onArchive={handleArchive}
                archiving={archivingId === config.id}
              />
            ))}
          </div>
        )}
      </div>

      {/* Create modal */}
      {showCreateModal && currentWorkspaceId && (
        <CreateConfigModal
          workspaceId={currentWorkspaceId}
          userId={userId}
          onClose={() => setShowCreateModal(false)}
          onCreate={() => {
            setShowCreateModal(false);
            void fetchAllWithVisibility(currentWorkspaceId);
          }}
        />
      )}
    </div>
  );
}
